/**
 * Academy Learn hub taxonomy: levels, categories, courses and topics. Articles (content/academy/*.mdx)
 * name these ids in their frontmatter; the build checks them (vite/academy.ts).
 *
 * Plain data with no imports, so the build plugin and the scripts under apps/web/scripts can load it
 * with Node's type stripping as well as the app.
 */

export type Level = 'beginner' | 'intermediate' | 'advanced';
export type CategoryId = 'trading' | 'crypto' | 'markets';
export type CourseId = 'beginner' | 'intermediate' | 'advanced' | 'platform-guide';
export type TopicId =
  | 'bitcoin'
  | 'ethereum'
  | 'altcoins'
  | 'stablecoins'
  | 'cfds'
  | 'leverage-margin'
  | 'risk-management'
  | 'technical-analysis'
  | 'indicators'
  | 'chart-patterns'
  | 'trading-strategies'
  | 'commodities'
  | 'stocks'
  | 'macro-news'
  | 'psychology'
  | 'security';

export interface LevelInfo {
  id: Level;
  label: string;
}

export const LEVELS: readonly LevelInfo[] = [
  { id: 'beginner', label: 'Beginner' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'advanced', label: 'Advanced' },
];

export interface CategoryInfo {
  id: CategoryId;
  title: string;
  blurb: string;
}

export const CATEGORIES: readonly CategoryInfo[] = [
  { id: 'trading', title: 'Trading', blurb: 'Orders, charts, risk and the habits that keep a trader in the game.' },
  { id: 'crypto', title: 'Crypto', blurb: 'Bitcoin, Ethereum, altcoins and stablecoins, and how their markets behave.' },
  { id: 'markets', title: 'Markets', blurb: 'CFDs on commodities, stocks and indices, and the news that moves them.' },
];

export interface CourseInfo {
  id: CourseId;
  title: string;
  /** One line for cards and menus. */
  blurb: string;
  /** Longer text for the course page. */
  intro: string;
  /** Level badge shown on the course card; the platform guide mixes levels. */
  level: Level | null;
}

export const COURSES: readonly CourseInfo[] = [
  {
    id: 'beginner',
    title: 'Beginner Path',
    blurb: 'Markets, CFDs, orders, leverage and your first trade.',
    intro: 'Start here if you are new to trading. Fifteen lessons take you from what a market is to placing and protecting your first trade, with every number worked through in USDT.',
    level: 'beginner',
  },
  {
    id: 'intermediate',
    title: 'Intermediate Path',
    blurb: 'Chart reading, indicators, position sizing and a trading plan.',
    intro: 'For traders who know the basics. Learn to read structure and momentum, size positions from your risk, trade commodities and stock CFDs, and keep a plan and journal.',
    level: 'intermediate',
  },
  {
    id: 'advanced',
    title: 'Advanced Path',
    blurb: 'Price action, liquidity, risk math, testing and discipline at scale.',
    intro: 'Deeper material for experienced traders: price action without indicators, liquidity concepts, expectancy and drawdown, scaling, testing a strategy and staying disciplined over hundreds of trades.',
    level: 'advanced',
  },
  {
    id: 'platform-guide',
    title: 'Platform Guide',
    blurb: 'How each screen works: account, orders, TP / SL, margin, chart, withdrawals.',
    intro: 'Step-by-step guides to the platform itself: setting up and funding your account, placing and managing orders, reading margin, using the chart and withdrawing safely.',
    level: null,
  },
];

export interface TopicInfo {
  id: TopicId;
  title: string;
  blurb: string;
  /** Lower-case words that mark an article as being about this topic when its frontmatter lists none. */
  keywords: readonly string[];
}

export const TOPICS: readonly TopicInfo[] = [
  { id: 'bitcoin', title: 'Bitcoin', blurb: 'The first and largest cryptocurrency, and what moves its price.', keywords: ['bitcoin', 'btc'] },
  { id: 'ethereum', title: 'Ethereum', blurb: 'Ether, smart contracts and the second-largest crypto market.', keywords: ['ethereum', 'ether'] },
  { id: 'altcoins', title: 'Altcoins', blurb: 'Coins beyond Bitcoin: higher volatility, thinner markets, bigger swings.', keywords: ['altcoin'] },
  { id: 'stablecoins', title: 'Stablecoins', blurb: 'USDT and other dollar tokens: how they hold their value and where they can fail.', keywords: ['stablecoin', 'usdt'] },
  { id: 'cfds', title: 'CFDs', blurb: 'Contracts for difference: trading price moves without owning the asset.', keywords: ['cfd'] },
  { id: 'leverage-margin', title: 'Leverage & Margin', blurb: 'How leverage multiplies exposure, what margin holds and when stop-out happens.', keywords: ['leverage', 'margin'] },
  { id: 'risk-management', title: 'Risk Management', blurb: 'Stops, position size and the math that keeps losses survivable.', keywords: ['stop loss', 'risk'] },
  { id: 'technical-analysis', title: 'Technical Analysis', blurb: 'Reading price: levels, trends, structure and timeframes.', keywords: ['support', 'resistance', 'trend'] },
  { id: 'indicators', title: 'Indicators', blurb: 'Moving averages, RSI, MACD, ATR and Bollinger Bands, used with context.', keywords: ['indicator', 'rsi', 'macd'] },
  { id: 'chart-patterns', title: 'Chart Patterns', blurb: 'Candlestick and chart patterns, and when they mean something.', keywords: ['pattern'] },
  { id: 'trading-strategies', title: 'Trading Strategies', blurb: 'Breakouts, mean reversion, trading styles and how to test them.', keywords: ['strategy', 'strategies'] },
  { id: 'commodities', title: 'Commodities', blurb: 'Gold, silver and oil CFDs: sessions, drivers and costs.', keywords: ['gold', 'oil', 'silver', 'commodit'] },
  { id: 'stocks', title: 'Stocks', blurb: 'Share and index CFDs: earnings, gaps and market hours.', keywords: ['stock', 'shares', 'earnings'] },
  { id: 'macro-news', title: 'Macro & News', blurb: 'Inflation, interest rates, data releases and crypto events.', keywords: ['cpi', 'interest rate', 'inflation'] },
  { id: 'psychology', title: 'Psychology', blurb: 'Fear, greed, tilt and the routines that keep decisions clean.', keywords: ['psychology', 'fomo', 'discipline'] },
  { id: 'security', title: 'Security', blurb: 'Two-factor authentication, phishing and keeping your account safe.', keywords: ['2fa', 'phishing', 'scam'] },
];

/** Home page "Platform Guide" tabs: the articles shown under each, in order (missing slugs are skipped). */
export const GUIDE_TABS: readonly { id: string; label: string; slugs: readonly string[] }[] = [
  { id: 'start', label: 'Getting Started', slugs: ['account-setup-deposits', 'platform-tour', 'your-first-trade', 'security-basics'] },
  { id: 'cfd', label: 'CFD Trading', slugs: ['what-is-a-cfd', 'placing-orders', 'long-vs-short', 'trading-costs-swaps-fees'] },
  { id: 'risk', label: 'Risk Tools', slugs: ['setting-tp-sl-trailing-stops', 'margin-leverage-liquidation-price', 'stop-loss-take-profit', 'liquidation-and-margin-calls'] },
  { id: 'advanced', label: 'Advanced Tools', slugs: ['charting-tools-indicators', 'isolated-vs-cross-margin', 'scaling-trailing-stops-partial-take-profit', 'withdrawals-fees-security'] },
];

/** Home page "Featured topics": three large tiles. */
export const FEATURED_TOPICS: readonly TopicId[] = ['risk-management', 'technical-analysis', 'leverage-margin'];

export const levelInfo = (id: Level) => LEVELS.find((l) => l.id === id)!;
export const categoryInfo = (id: string) => CATEGORIES.find((c) => c.id === id) ?? null;
export const courseInfo = (id: string) => COURSES.find((c) => c.id === id) ?? null;
export const topicInfo = (id: string) => TOPICS.find((t) => t.id === id) ?? null;

/** Lower-case, dashes, no punctuation: "Stop Loss & Take Profit" → "stop-loss-take-profit". */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Closes every article (and the static HTML the build writes for it). */
export const RISK_REMINDER =
  'Trading CFDs and crypto involves a high risk of losing money quickly because of leverage. You can lose all the funds in your account. This article is for education only and is not financial advice; past performance does not indicate future results.';

/** Footer of every Learn hub page. */
export const RISK_WARNING =
  'Risk warning: CFDs and cryptocurrencies are complex instruments and come with a high risk of losing money rapidly due to leverage. You can lose all of the funds you deposit. Only trade with money you can afford to lose, and make sure you understand how these products work. Academy content is for education only and is not financial advice, an offer or a recommendation to buy or sell any instrument. Past performance is not indicative of future results.';
